import { MixedChartInterface, MixedChartOptionsInterface } from '../../../types/interfaces/charts.interface';
import {TranslateService} from '@ngx-translate/core';

export function createWaterMeasurementChartData(translateService: TranslateService): MixedChartInterface {
  return {
    loading: true,
    labels: [],
    datasets: [
      {
        label: translateService.instant('common.title.water-consumption') + " (m³)",
        type: 'line',
        borderWidth: 1,
        borderColor: 'rgb(75, 192, 192)',
        data: [],
        fill: true,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0,
        yAxisID: 'y',
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
      },
      {
        label: translateService.instant('common.title.water-consumption') + " (" + translateService.instant('common.label.per_day') + ")",
        type: 'bar',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        data: [],
        borderWidth: 1,
        borderColor: 'rgb(255, 99, 132)',
        yAxisID: 'y1',
      },
    ],
  };
}

export function createWaterMeasurementChartOptions(): MixedChartOptionsInterface {
  const bodyStyle: CSSStyleDeclaration = getComputedStyle(document.body);
  const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);

  const textColor: string = getCssVariable(
    bodyStyle,
    rootStyle,
    [
      '--text-color',
      '--text-primary',
    ],
    '#111827',
  );

  return {
    animation: false,
    maintainAspectRatio: true,
    interaction: {
      mode: 'index',
      intersect: false,
      axis: 'x',
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: textColor,
        },
        onClick: (e: any, legendItem: any, legend: any) => {
          const chart = legend.chart;
          const datasetIndex = legendItem.datasetIndex;

          chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));

          const firstVisible = chart.isDatasetVisible(0);
          const secondVisible = chart.isDatasetVisible(1);

          chart.options.scales.y.display = firstVisible;

          if (chart.options.scales.y1) {
            chart.options.scales.y1.display = secondVisible;
            chart.options.scales.y1.grid.drawOnChartArea = !firstVisible;
          }

          chart.update();
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (items: any[]) => items?.[0]?.label ?? '',
          label: (context: any) => {
            const label = context.dataset.label ?? '';
            const value = context.parsed.y ?? 0;

            return `${ label }: ${ value }`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
        },
        grid: {
          color: '#A6A6A655',
        },
      },
      y: {
        type: 'linear',
        position: 'left',
        display: true,
        ticks: {
          color: textColor,
        },
        grid: {
          color: '#A6A6A655',
        },
      },
      y1: {
        type: 'linear',
        position: 'right',
        display: true,
        ticks: {
          color: textColor,
        },
        grid: {
          color: '#A6A6A655',
          drawOnChartArea: false,
        },
      },
    },
  };
}

function getCssVariable(
  bodyStyle: CSSStyleDeclaration,
  rootStyle: CSSStyleDeclaration,
  variableNames: string[],
  fallback: string,
): string {
  for (const variableName of variableNames) {
    const bodyValue: string = bodyStyle.getPropertyValue(variableName)
      .trim();

    if (bodyValue !== '') {
      return bodyValue;
    }

    const rootValue: string = rootStyle.getPropertyValue(variableName)
      .trim();

    if (rootValue !== '') {
      return rootValue;
    }
  }

  return fallback;
}
